/**
 * Legacy pages Agent Studio has taken over. Each `link-outs/<area>.ts` file
 * default-exports the routes for its area; add it to `LINK_OUT_ROUTES`.
 *
 * Studio honors dev-tools flag overrides for any user but Agent Studio only for
 * admins, so a non-admin override can link into a page that bounces.
 * `AGENT_STUDIO_AUDIENCES` targeting must key on account id only: Agent Studio
 * may identify without `tier`/`createdAt`, and a mismatch bounces the linked
 * user.
 */
import audienceRoutes from './link-outs/audience';
import experienceRoutes from './link-outs/experiences';
import insightRoutes from './link-outs/insights';
import npsRoutes from './link-outs/nps';
import segmentRoutes from './link-outs/segments';
import settingRoutes from './link-outs/settings';
import type {
  LinkOutContent,
  LinkOutFlag,
  LinkOutRoute,
} from './link-outs/types';

export type { LinkOutFlag };

export type LinkOutFlagValues = Record<LinkOutFlag, boolean | null>;

export type LinkOutGate = 'link-out' | 'legacy' | 'pending';

export type LinkOutTarget = {
  path: string;
  content: LinkOutContent;
  flags: LinkOutFlag[];
};

export type AgentStudioNavigation =
  | { kind: 'none' }
  | { kind: 'pending' }
  | { kind: 'link-out'; path: string; content: LinkOutContent };

export const LINK_OUT_ROUTES: LinkOutRoute[] = [
  ...segmentRoutes,
  ...audienceRoutes,
  ...experienceRoutes,
  ...insightRoutes,
  ...settingRoutes,
  ...npsRoutes,
];

/** The Agent Studio page for a legacy page, if `pathname` is one. */
export function getAgentStudioLinkOut(
  pathname: string
): LinkOutTarget | undefined {
  for (const { pattern, resolve, content, flags = [] } of LINK_OUT_ROUTES) {
    const match = pattern.exec(pathname);
    if (match) {
      return { path: resolve(match), content, flags };
    }
  }

  return undefined;
}

export function resolveLinkOutGate(values: (boolean | null)[]): LinkOutGate {
  if (values.includes(false)) {
    return 'legacy';
  }

  return values.every(Boolean) ? 'link-out' : 'pending';
}

/** Query and hash are dropped on purpose: legacy params mean nothing in Agent Studio. */
export function resolveAgentStudioNavigation(
  pathname: string,
  values: LinkOutFlagValues
): AgentStudioNavigation {
  const target = getAgentStudioLinkOut(pathname);
  if (!target) {
    return { kind: 'none' };
  }

  switch (resolveLinkOutGate(target.flags.map(flag => values[flag]))) {
    case 'link-out':
      return { kind: 'link-out', path: target.path, content: target.content };
    case 'pending':
      return { kind: 'pending' };
    default:
      return { kind: 'none' };
  }
}
